import type { ReactElement } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';

import { useBetriebshinweise, useNeustartBestaetigen } from '../../admin/hinweiseApi';
import { useAbmelden, useAdminSitzung } from '../../admin/useAdminSitzung';
import { Logo } from '../../components/Logo';
import { ThemeToggle } from '../../components/ThemeToggle';
import { useEchtzeit } from '../../echtzeit/useEchtzeit';
import { formatiereZeitpunkt } from '../../formatierung';
import { istBereichAktiv } from './navigation';

/**
 * Die Hauptbereiche des Administrationsbereichs (§20.1).
 *
 * Seit S8 ist jeder Bereich aus §20.1 mit einer echten Seite hinterlegt; die Stufenkennzeichnung
 * der Platzhalter ist damit entfallen.
 *
 * „Teilnehmer und Tokens" aus §20.1 erscheint bewusst nicht als eigener Punkt: Tokens gehören
 * nach §7.4 immer zu genau einer Veranstaltung — ihr Veranstaltungscode ist Bestandteil jedes
 * Tokens — und sind deshalb aus der jeweiligen Veranstaltung erreichbar. Die Teilnehmer- und
 * Sitzungsverwaltung nach §9.8 liegt dort und ist zusätzlich aus der Durchführung verlinkt.
 *
 * „Durchführung" (§20.3) ist ein eigener Punkt und keine Unterseite der Veranstaltung: Sie zeigt
 * immer die **eine** freigegebene oder laufende Veranstaltung (§6.3) und ist damit während einer
 * Sitzung der Ort, an dem der Administrator bleibt.
 *
 * „Personalisierung" und „E-Mail-Versand" stehen als letzte Punkte und gehen über §20.1 hinaus:
 * §40.4 schließt eine Brandingverwaltung im Backend aus (ÄA-5), §26 schließt externe Dienste aus
 * (ÄA-7). Beide Abweichungen sind freigegeben. Der Platz am Ende ist gewollt — die Angaben werden
 * einmal gepflegt und danach kaum wieder angefasst.
 */
const BEREICHE = [
  { pfad: 'uebersicht', name: 'Dashboard' },
  { pfad: 'durchfuehrung', name: 'Durchführung' },
  { pfad: 'veranstaltungen', name: 'Veranstaltungen' },
  { pfad: 'abstimmungen', name: 'Abstimmungen' },
  { pfad: 'ergebnisse', name: 'Ergebnisse und Exporte' },
  { pfad: 'backups', name: 'Backups und Wiederherstellung' },
  { pfad: 'protokoll', name: 'Änderungsprotokoll' },
  { pfad: 'systemstatus', name: 'Systemstatus' },
  { pfad: 'personalisierung', name: 'Personalisierung' },
  { pfad: 'email-versand', name: 'E-Mail-Versand' },
] as const;

/**
 * Hülle des Administrationsbereichs.
 *
 * Ohne gültige Sitzung wird zur Anmeldung geführt (§25.2). Läuft die Sitzung ab, geschieht das
 * ebenfalls — mit einem Hinweis, damit der Wechsel nicht unerklärt bleibt (§5.2).
 */
export function AdminLayout(): ReactElement {
  const sitzung = useAdminSitzung();
  const abmelden = useAbmelden();
  const ort = useLocation();

  // Der Echtzeitkanal des Administrationsbereichs (§22.1). Er hängt an der Hülle und nicht an
  // einzelnen Seiten: So besteht **eine** Verbindung, gleich welche Seite geöffnet ist, und ein
  // Seitenwechsel baut sie nicht ab und wieder auf. Ein Socket verlängert die 30-Minuten-Frist
  // ausdrücklich nicht (§5.2) — dafür sorgt der Server.
  useEchtzeit('admin', sitzung.isSuccess);

  if (sitzung.isPending) {
    return (
      <main className="flex flex-1 items-center justify-center p-6">
        <p className="text-text-gedaempft">Sitzung wird geprüft …</p>
      </main>
    );
  }

  if (sitzung.isError) {
    const abgelaufen = sitzung.error.code === 'SITZUNG_ABGELAUFEN';
    return (
      <Navigate
        to={`/verwaltung${abgelaufen ? '?grund=abgelaufen' : ''}`}
        replace
        state={{ von: ort.pathname }}
      />
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <header className="border-rand bg-flaeche flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div>
          <Logo />
          <p className="text-text-gedaempft text-xs">Verwaltung</p>
        </div>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => {
              abmelden.mutate();
            }}
            disabled={abmelden.isPending}
            className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand rounded-md border px-3 text-sm font-medium disabled:opacity-60"
          >
            {abmelden.isPending ? 'Abmelden …' : 'Abmelden'}
          </button>
        </div>
      </header>

      <Betriebshinweise aktiv={sitzung.isSuccess} />

      <div className="flex flex-1 flex-col gap-4 p-4 md:flex-row">
        <nav aria-label="Bereiche" className="md:w-64 md:shrink-0">
          <ul className="flex flex-col gap-1">
            {BEREICHE.map((bereich) => (
              <li key={bereich.pfad}>
                <NavLink
                  to={bereich.pfad}
                  // Die Markierung kommt aus `istBereichAktiv()` statt aus `isActive`: die
                  // Abstimmungen einer Veranstaltung liegen unter deren Pfad, gehören aber zum
                  // Punkt „Abstimmungen" (§20.1).
                  className={[
                    'min-h-touchflaeche flex items-center justify-between gap-2 rounded-md px-3 text-sm transition',
                    istBereichAktiv(bereich.pfad, ort.pathname)
                      ? 'bg-akzent text-akzent-text font-medium'
                      : 'text-text hover:bg-flaeche-gedaempft',
                  ].join(' ')}
                >
                  <span>{bereich.name}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Hinweisband über allen Adminseiten (§25.5, §35.3).
 *
 * Zwei Anlässe, die den Administrator **erreichen** müssen: ein unerwarteter Neustart und ein
 * unverschlüsselter Zugriff im Produktionsbetrieb. Ein Protokolleintrag allein genügt für §35.3
 * nicht — er drängt sich niemandem auf.
 *
 * Nur der Neustarthinweis lässt sich bestätigen. Der HTTPS-Hinweis beschreibt einen
 * fortbestehenden Zustand und wäre mit einem Klick weggeräumt, ohne dass sich etwas geändert
 * hätte (§25.5).
 */
function Betriebshinweise({ aktiv }: { aktiv: boolean }): ReactElement | null {
  const hinweise = useBetriebshinweise(aktiv);
  const bestaetigen = useNeustartBestaetigen();

  const daten = hinweise.data;
  if (daten === undefined) {
    return null;
  }

  const neustart = daten.unerwarteterNeustart;
  if (neustart === null && !daten.unsichererZugriff) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 px-4 pt-4">
      {neustart !== null && (
        <div className="border-warnung/50 bg-warnung/10 flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm">
          <p>
            <strong>Unerwarteter Neustart.</strong> Der vorherige Lauf endete am{' '}
            {formatiereZeitpunkt(neustart)} nicht geordnet. Die Integrität der Datenbank wurde
            geprüft; fachliche Statuswerte sind unverändert. Einzelheiten stehen im
            Änderungsprotokoll.
          </p>
          <button
            type="button"
            onClick={() => {
              bestaetigen.mutate();
            }}
            disabled={bestaetigen.isPending}
            className="border-rand bg-flaeche min-h-touchflaeche hover:bg-rand shrink-0 rounded-md border px-3 text-sm font-medium disabled:opacity-60"
          >
            Zur Kenntnis genommen
          </button>
        </div>
      )}

      {daten.unsichererZugriff && (
        <p className="border-fehler/50 bg-fehler/10 rounded-md border p-3 text-sm">
          <strong>Unverschlüsselter Zugriff.</strong> Diese Sitzung läuft über HTTP. Für den
          öffentlichen Betrieb wird HTTPS ausdrücklich empfohlen — Zugangsdaten und Sitzungscookies
          werden sonst im Klartext übertragen.
        </p>
      )}
    </div>
  );
}
